import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { FAVORITE_COUNTS_QUERY_KEY } from './favorites/useFavoriteCounts';

/** The three Flow tabs whose list rows a mutation can change. */
export type FlowListEntity = 'account' | 'contact' | 'signal';

/** The query-key prefix each tab's list (every filter / sort / groupBy variant) lives under. */
export const FLOW_LIST_QUERY_ROOT = {
  account: ['flow-accounts'],
  contact: ['flow-contacts'],
  signal: ['flow-signals'],
} as const satisfies Record<FlowListEntity, readonly string[]>;

/**
 * The keys a row-changing mutation must refresh: each named tab's list prefix, in the order given, then EVERY
 * favorite count. A saved view's count is "rows in that view", so any change to a tab's rows can move it, and
 * `['flow-favorite-counts']` does not prefix-match a list key.
 */
export function flowListInvalidationKeys(...entities: FlowListEntity[]): QueryKey[] {
  return [...entities.map(entity => [...FLOW_LIST_QUERY_ROOT[entity]]), [...FAVORITE_COUNTS_QUERY_KEY]];
}

/** Invalidates `flowListInvalidationKeys(...entities)`: the one call a mutation that changes list rows makes. */
export async function invalidateFlowList(queryClient: QueryClient, ...entities: FlowListEntity[]): Promise<void> {
  await Promise.all(flowListInvalidationKeys(...entities).map(queryKey => queryClient.invalidateQueries({ queryKey })));
}
